import type {
  ConnectionReviewState,
  ReviewRecommendation,
} from '@@/packages/shared/types/models/connectionReview';

export default {
  en: {
    states: {
      not_reviewed: 'Not reviewed',
      in_review: 'In review',
      reviewed: 'Reviewed',
      needs_re_review: 'Needs re-review',
    } satisfies Record<ConnectionReviewState, string>,
    startPrompt: {
      title: 'Start a supplier review?',
      description: {
        controlReviewed:
          "You're reviewing controls, but currently there is no open review for this supplier. Would you like to start one?",
        userAssigned:
          "You're assigning colleagues to controls, but currently there is no open review for this supplier. Would you like to start one?",
      },
      accept: 'Start review',
      decline: 'Continue without',
    },
    button: {
      start: 'Start review',
      continue: 'Continue review',
      open: 'Open reviews',
      newReview: 'New review',
      startError: "Couldn't start the review. Try again.",
    },
    startAssignments: {
      title: 'Start review',
      reReviewTitle: 'Start a re-review',
      assigned:
        "You have users assigned to this supplier's assessment controls. How would you like to proceed?",
      required: 'Select an option',
      reset: 'Keep the users assigned and reset reviewed controls',
      keep: 'Keep the users assigned without resetting the control reviews',
      clear: 'Clear all control assignments',
      start: 'Start',
      cancel: 'Cancel',
      error: "Couldn't update the control assignments. Try again.",
    },
    banner: {
      blocked: 'Blocked',
      decisionsPending: '{n} decision pending | {n} decisions pending',
      reviewed: {
        approve: 'Reviewed - Approved',
        reject: 'Reviewed - Rejected',
      } satisfies Record<ReviewRecommendation, string>,
    },
    expiry: {
      label: 'Review expiry date',
      edit: 'Edit review expiry date',
      options: {
        none: 'No re-review date',
        oneMonth: 'In 1 month',
        threeMonths: 'In 3 months',
        sixMonths: 'In 6 months',
        oneYear: 'In 1 year',
        eighteenMonths: 'In 18 months',
        twoYears: 'In 2 years',
        custom: 'Specific date',
      },
      customDateLabel: 'Date',
      errors: {
        customDateRequired: 'Enter a date',
      },
      error: "Couldn't save the expiry date. Try again.",
    },
    drawer: {
      title: 'Supplier reviews',
      tabs: {
        currentReview: 'Current review',
        lastReview: 'Last review',
      },
      missingReview: {
        title: 'This review no longer exists',
        viewCurrent: 'View current review',
        viewLast: 'View last review',
      },
      metadata: {
        startDate: 'Started',
        lastEdit: 'Last edited',
        completedDate: 'Completed',
        by: 'by {name}',
      },
      fields: {
        reviewStage: 'Review stage',
        recommendation: 'Recommendation',
      },
      stages: {
        in_progress: 'In progress',
        blocked: 'Blocked',
        awaiting_decision: 'Awaiting decisions',
        completed: 'Completed',
      },
      blockedBy: 'Blocked by {name}',
      blockReason: 'Reason',
      recommendations: {
        approve: 'Approve',
        reject: 'Reject',
        none: 'None',
      },
      sections: {
        decisions: 'Decisions',
        notes: 'Review notes',
      },
      decisionStatus: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        undetermined: 'Undetermined / No decision',
      },
      decisions: {
        empty: 'No decisions',
      },
      notes: {
        empty: 'No review notes',
        placeholder: 'Write your notes here',
      },
      actions: {
        requestDecision: 'Request decision',
        manageDecisions: 'Manage decisions',
        viewDecisions: 'View decisions',
        markAsReviewed: 'Mark as reviewed',
        respond: 'Respond',
        editResponse: 'Edit response',
        deleteReview: 'Delete review',
        block: 'Mark as blocked',
        unblock: 'Unblock',
        markAsReviewedBlocked: 'Unblock the review before marking it as reviewed',
      },
      recap: {
        title: 'Review recap',
        generating: 'Generating PDF',
        download: 'Download PDF',
        downloadError: "Couldn't download the review recap. Try again.",
        failed: {
          status: 'Failed',
          tooltip:
            "Couldn't create the review recap. Contact support and we can create it for you.",
          contactSupport: 'Contact support',
        },
        supportMessage:
          "The recap for one of my supplier reviews couldn't be created. Review ID: {reviewID}",
      },
      editError: "Couldn't save your change. Try again.",
      notesError: "Couldn't save your notes. Try again.",
      blockModal: {
        title: 'Mark as blocked',
        description:
          "You'll need to unblock the review before marking it as reviewed. All other actions stay available.",
        reasonLabel: 'Blocked reason',
        reasonPlaceholder: 'Enter a reason',
        reasonRequired: 'Enter a reason for blocking the review',
        confirm: 'Mark as blocked',
        cancel: 'Cancel',
      },
      deleteModal: {
        title: 'Delete review',
        description:
          'The supplier reverts to its previous status. You lose everything attached to this review, including approvals, notes, and attachments.',
        confirm: 'Delete review',
        error: "Couldn't delete the review. Try again.",
      },
      completeModal: {
        title: 'Finalise review',
        description:
          'This action finalises your notes and completes all approvals. Pending approvals will be shown as Undetermined/No decision.',
        warning: "You won't be able to edit this review after completion.",
        expiryLabel: 'Set re-review date due (optional)',
        includeAISummary: 'Include the AI summary in the review PDF',
        aiSummaryReused: 'The existing summary ({age}) will be used.',
        confirm: 'Mark as reviewed',
        cancel: 'Cancel',
        error: "Couldn't complete the review. Try again.",
      },
    },
    addApprovers: {
      title: 'Request decision',
      description: "Select people to add as approvers to this supplier's review",
      tabs: {
        internal: 'Risk Ledger users',
        external: 'Outside Risk Ledger',
      },
      colleaguesLabel: 'Colleagues',
      colleaguesPlaceholder: 'Select colleagues',
      colleaguesEmpty: 'Everyone already has a decision on this review',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter an email address',
      recommendationLabel: 'Recommendation',
      notesLabel: 'Notes',
      notesPlaceholder: 'Write your notes here',
      included: {
        label: 'Included',
        title: 'Select options to include ({count} selected)',
        none: 'Nothing selected',
        compliance_score: 'Compliance score',
        non_compliant_controls: 'Non-compliant controls',
        open_risks: 'Open risks',
        profile_notes: 'Profile notes',
        save: 'Save',
        cancel: 'Cancel',
      },
      errors: {
        colleaguesRequired: 'Select at least one colleague',
        emailRequired: 'Enter an email address',
        emailInvalid: 'Enter a valid email address',
        includedDataRequired: 'Select at least one option',
        duplicate: 'This approver already has a decision on this review',
        generic: "Couldn't request the decision. Try again.",
      },
      confirm: 'Send request | Send request | Send requests',
      cancel: 'Cancel',
    },
    manageDecisions: {
      title: 'Manage decisions',
      description:
        'These are the decisions you have requested so far. Removing a decision erases all its information.',
      completed: {
        title: 'View decisions',
        description:
          'These are the decisions on this review. Decisions on a completed review cannot be changed.',
      },
      requestedBy: 'Requested by',
      viewRequestDetails: 'View request details',
      recommendation: 'Recommendation',
      requestNotes: 'Request notes',
      noRequestNotes: 'No request notes',
      responseNotes: 'Response notes',
      remove: 'Remove',
      removeModal: {
        title: 'Remove decision',
        description:
          'You will lose everything attached to this decision, including any notes and attachments.',
      },
      removeError: "Couldn't remove the decision. Try again.",
    },
    recordDecision: {
      title: 'Decision request',
      editTitle: 'Edit response',
      instruction:
        "Mark as approved or rejected in this supplier's review. If the review is finalised before you respond it will show as Undetermined/No decision.",
      requestedBy: 'Requested by',
      summary: 'Summary',
      noSummary: 'No summary provided',
      recommendation: 'Recommendation',
      yourResponse: 'Your response',
      yourResponseHelp: 'Make your approval decision by selecting one of the options below.',
      commentPlaceholder: 'Add a comment (optional)',
      approve: 'Approve',
      reject: 'Reject',
      decision: 'Decision',
      save: 'Save changes',
      cancel: 'Cancel',
      recordError: "Couldn't record your decision. Try again.",
      attachmentsUploading: 'Wait for your attachments to finish uploading',
    },
    activityItems: {
      reviewStarted: '{name} started a review',
      reviewCompleted: '{name} completed the review',
      reviewDeleted: '{name} deleted the review',
      reviewBlocked: {
        withReason: '{name} blocked the review. {reasonLabel} {reason}',
        reasonLabel: 'Reason:',
        noReason: '{name} blocked the review',
      },
      reviewUnblocked: '{name} unblocked the review',
      reviewExpiryChanged: {
        set: '{name} set the review expiry date to {date}',
        moved: '{name} moved the review expiry date from {previousDate} to {date}',
        cleared: '{name} removed the review expiry date',
      },
      decisionRequested: '{name} requested a decision from {approver}',
      decisionDecided: {
        responded: '{name} responded to a decision request with: {decision}',
        decision: {
          approved: 'Approve',
          rejected: 'Reject',
        },
        pending: '{name} updated their decision',
        externalApprover: 'An external approver',
      },
      decisionRemoved: '{name} removed a decision request for {approver}',
      decisionGroup: {
        decision_requested_group: '{name} requested decisions from {approvers}',
        decision_removed_group: '{name} removed decision requests for {approvers}',
        separator: ', ',
        lastSeparator: ' and ',
      },
      initialReview: {
        message: 'Review status set to {state}',
        state: {
          in_review: 'in review',
          reviewed: 'reviewed',
          needs_re_review: 'needs a re-review',
        },
      },
      approver: {
        colleague: 'a colleague',
        external: 'an external approver',
      },
    },
  },
};
